import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  InspectableSession,
  InspectionAttachment,
  InspectionCalendarQuery,
  InspectionOptions,
  InspectionQuery,
  SessionInspection,
} from '../interfaces/inspection';
import {
  ApiPaginatedResponse,
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';

/**
 * Inspection des séances.
 *
 * Endpoints :
 *   GET    /api/inspector/sessions?from&to&professorId&subjectId&inspected&page&pageSize
 *   GET    /api/inspector/sessions/{id}
 *   PUT    /api/inspector/sessions/{id}/inspection
 *   DELETE /api/inspector/sessions/{id}/inspection
 */
@Injectable({ providedIn: 'root' })
export class InspectionService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.BASE_URL}/inspector/sessions`;

  search(query: InspectionQuery = {}): Observable<ApiPaginatedResponse<InspectableSession>> {
    let params = new HttpParams();

    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    if (query.professorId) params = params.set('professorId', query.professorId);
    if (query.subjectId) params = params.set('subjectId', query.subjectId);
    // Comparaison stricte : `false` est un filtre à part entière — c'est même
    // le plus utile, celui de ce qui reste à inspecter.
    if (query.inspected != null) params = params.set('inspected', query.inspected);
    if (query.page) params = params.set('page', query.page);
    if (query.pageSize) params = params.set('pageSize', query.pageSize);

    return this.http.get<ApiPaginatedResponse<InspectableSession>>(this.base, { params });
  }

  /**
   * Listes des filtres et périmètre de l'inspecteur — sous la permission de
   * l'écran. Le référentiel de planification, réservé au superviseur,
   * répondait 403 à un professeur-inspecteur.
   */
  /**
   * Mon agenda : les séances d'une période (62 jours au plus), dans mon
   * périmètre, sans les miennes — même règle que la liste, sans pagination.
   */
  calendar(query: InspectionCalendarQuery): Observable<ApiCollectionResponse<InspectableSession>> {
    let params = new HttpParams().set('from', query.from).set('to', query.to);
    if (query.professorId) params = params.set('professorId', query.professorId);
    if (query.subjectId) params = params.set('subjectId', query.subjectId);
    if (query.inspected !== null && query.inspected !== undefined) params = params.set('inspected', query.inspected);
    return this.http.get<ApiCollectionResponse<InspectableSession>>(`${this.base}/calendar`, { params });
  }

  options(): Observable<ApiSingleResponse<InspectionOptions>> {
    return this.http.get<ApiSingleResponse<InspectionOptions>>(`${this.base}/options`);
  }

  getById(id: number): Observable<ApiSingleResponse<InspectableSession>> {
    return this.http.get<ApiSingleResponse<InspectableSession>>(`${this.base}/${id}`);
  }

  /** Dépose ou remplace mon rapport. Marquer inspecté et rédiger sont le même geste. */
  saveReport(id: number, report: string): Observable<ApiSingleResponse<SessionInspection>> {
    return this.http.put<ApiSingleResponse<SessionInspection>>(
      `${this.base}/${id}/inspection`,
      { report },
    );
  }

  removeReport(id: number): Observable<ApiSingleResponse<boolean>> {
    return this.http.delete<ApiSingleResponse<boolean>>(`${this.base}/${id}/inspection`);
  }

  /** Joint un document à mon rapport — qui doit déjà exister. */
  addAttachment(id: number, file: File): Observable<ApiSingleResponse<InspectionAttachment>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ApiSingleResponse<InspectionAttachment>>(
      `${this.base}/${id}/inspection/attachments`,
      form,
    );
  }

  removeAttachment(id: number, attachmentId: number): Observable<ApiSingleResponse<boolean>> {
    return this.http.delete<ApiSingleResponse<boolean>>(
      `${this.base}/${id}/inspection/attachments/${attachmentId}`,
    );
  }
}
